import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
  keepPreviousData,
  type QueryClient,
} from "@tanstack/react-query";
import type { FragmentCountQualifier } from "../utils/fragmentCount";
import {
  accessioningService,
  grossingService,
  embeddingService,
  cuttingService,
  stainingService,
  distributionService,
  scannerService,
  pathologistReviewService,
  qaReviewService,
  qaReviewDetailService,
  shippingService,
  errorReportsService,
  caseLockService,
  barcodeService,
  controlConfirmationService,
  controlSlidesService,
  reportDeliveryQueueService,
  qaMetricsService,
  stainMetricsService,
  userPreferencesService,
  WorkflowQueueCase,
  AccessioningData,
  GrossingBlock,
  SpecimenGrossingUpdate,
  ControlConfirmationStatus,
  HeConfirmationDetail,
  PracticeForConfirmation,
  ErrorReportFilters,
  ErrorReport,
  PathologistReviewQueueFilters,
  PathologistIncomingQueueFilters,
  PathologistCompletedQueueFilters,
  PathologistReviewQueueCase,
  PathologistReviewStats,
  IncomingLabQueueResponse,
  QaReviewQueueFilters,
  QaReviewQueueItem,
  QaReviewQueueStats,
  QaReviewDetail,
  QaReviewUpdateData,
  QaReviewCompleteData,
  QaMetricsFilters,
  QaMetricsSummary,
  QaMetricsTrendPoint,
  QaMetricsByPathologist,
  QaMetricsByInstitution,
  DeliveryQueuePage,
  DeliveryQueuePageRequest,
  DeliveryQueueStats,
  DeliverySuppressionSettings,
  PendingShippingCase,
  ConfirmShippingResult,
  ShippedCasesFilters,
  ShippedCasesResponse,
  ShippingTrackingUpdate,
  StainMetricsFilters,
  StainMetricsSummary,
  StainMetricsTrends,
  PathologistComment,
  StainOfDayGroup,
  SpecimenMacroForAccessioning,
  BatchTemplateForAccessioning,
  DistributionQueueCounts,
  StainingSlidesParams,
  StainingSlidesResponse,
  insufficientTissueService,
  InsufficientTissueConfirmation,
  InsufficientTissueTarget,
} from "../services/workflows.service";
import { CaseFilters } from "../services/cases.service";
import {
  getActiveDeliveryMethodsForCase,
  queueManualDelivery,
  getDeliveryAdmins,
  CaseDeliveryMethodConfig,
  DeliveryAdmin,
} from "../services/signOff.service";
import { reportDeliveryRecallQueryKey } from "./useReportDeliveryRecall";

const insufficientTissueQueryRoots = new Set([
  "case-for-grossing",
  "case-for-embedding",
  "case-for-cutting",
  "case",
  "cases",
  "case-specimens",
  "case-slides",
  "case-blocks",
  "grossing-queue",
  "embedding-queue",
  "cutting-queue",
  "staining-slides",
  "staining-queue",
  "distribution-queue",
  "distribution-counts",
  "pending-distribution",
  "distribution-case-slides",
  "pathologist-review",
  "pathologist-review-queue",
  "pending-work",
  "pending-overview",
  "additional-stains",
  "case-panel-summary",
  "case-panel-orders",
  "billing-lines",
  "case-billing",
  "send-outs",
  "sendouts",
  "billing",
  "current-report-projection",
  "case-for-pathologist-review",
  "case-additional-stains",
  "stain-requests",
  "billing-stats",
  "billing-cases",
  "reports",
]);

function invalidateInsufficientTissueQueries(
  client: QueryClient
): Promise<void> {
  return client.invalidateQueries({
    predicate: (query) =>
      insufficientTissueQueryRoots.has(String(query.queryKey[0])),
  });
}

export function useSetSpecimenInsufficientTissue() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (
      input: InsufficientTissueTarget & {
        insufficientTissue: boolean;
        expectedVersion: number;
      }
    ) => insufficientTissueService.setFlag(input),
    onSuccess: () => invalidateInsufficientTissueQueries(client),
  });
}

export function useConfirmInsufficientTissue() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: InsufficientTissueConfirmation) =>
      insufficientTissueService.confirm(input),
    onSuccess: () => invalidateInsufficientTissueQueries(client),
  });
}

// ==================== WORKFLOW EVENTS (CROSS-TAB SYNC) ====================

export const WORKFLOW_EVENT_CHANNEL = "gcp-workflow-events";

const broadcastWorkflowEvent = (type: string, caseId?: string) => {
  if (typeof window !== "undefined" && "BroadcastChannel" in window) {
    try {
      const channel = new BroadcastChannel(WORKFLOW_EVENT_CHANNEL);
      channel.postMessage({ type, caseId });
      channel.close();
    } catch (e) {
      // Silently fail if broadcast fails
    }
  }
};

// ==================== ACCESSIONING ====================

export function useSpecimenMacrosForAccessioning() {
  return useQuery<SpecimenMacroForAccessioning[]>({
    queryKey: ["specimen-macros"],
    queryFn: () => accessioningService.getSpecimenMacros(),
    staleTime: 1000 * 60 * 30,
  });
}

export function useBatchTemplatesForAccessioning() {
  return useQuery<BatchTemplateForAccessioning[]>({
    queryKey: ["specimen-batch-templates"],
    queryFn: () => accessioningService.getBatchTemplates(),
    staleTime: 1000 * 60 * 30,
  });
}

export function useCreateDraftCase() {
  return useMutation({
    mutationFn: (facilityId: string) =>
      accessioningService.createDraftCase(facilityId),
  });
}

export function useUpdateAccessioningCase(options?: {
  onSuccess?: (
    data: any,
    variables: { caseId: string; data: AccessioningData }
  ) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      data,
    }: {
      caseId: string;
      data: AccessioningData;
    }) => accessioningService.updateCase(caseId, data),
    onSuccess: (data, variables) => {
      const { caseId } = variables;
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({ queryKey: ["case-specimens", caseId] });
      queryClient.invalidateQueries({ queryKey: ["case-blocks", caseId] });
      queryClient.invalidateQueries({ queryKey: ["case-slides", caseId] });
      // Also invalidate case files to reflect newly saved documents
      queryClient.invalidateQueries({
        queryKey: ["case-files", caseId],
      });

      // Linear C-4185: Invalidate distribution-related queries
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
      queryClient.invalidateQueries({
        queryKey: ["distribution-case-slides"],
      });
      queryClient.invalidateQueries({ queryKey: ["distribution-counts"] });

      // Call custom onSuccess if provided
      options?.onSuccess?.(data, variables);

      // Broadcast event for cross-tab synchronization
      broadcastWorkflowEvent("accessioning-updated", caseId);
    },
  });
}

export function useAssignAccessioningPrefix() {
  return useMutation({
    mutationFn: ({
      caseId,
      prefixCode,
    }: {
      caseId: string;
      prefixCode: string;
    }) => accessioningService.assignPrefix(caseId, prefixCode),
  });
}

export function useSynchronizeCanonicalExternalCaseNumber() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      externalCaseNumber,
      prefixCode,
    }: {
      caseId: string;
      externalCaseNumber: string | null;
      prefixCode?: string;
    }) =>
      accessioningService.synchronizeCanonicalExternalCaseNumber(caseId, {
        externalCaseNumber,
        prefixCode,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.invalidateQueries({ queryKey: ["case", data.caseId] });
    },
  });
}

export function useCompleteAccessioning(options?: {
  onSuccess?: (
    data: any,
    variables: { caseId: string; data: AccessioningData }
  ) => void;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      data,
    }: {
      caseId: string;
      data: AccessioningData;
    }) => accessioningService.completeAccessioning(caseId, data),
    onSuccess: (data, variables) => {
      const { caseId } = variables;
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({ queryKey: ["case-specimens", caseId] });
      queryClient.invalidateQueries({ queryKey: ["case-blocks", caseId] });
      queryClient.invalidateQueries({ queryKey: ["case-slides", caseId] });

      // Linear C-4185: Invalidate distribution-related queries
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
      queryClient.invalidateQueries({
        queryKey: ["distribution-case-slides"],
      });
      queryClient.invalidateQueries({ queryKey: ["distribution-counts"] });

      // Call custom onSuccess if provided
      options?.onSuccess?.(data, variables);

      // Broadcast event for cross-tab synchronization
      broadcastWorkflowEvent("accessioning-completed", caseId);
    },
  });
}

// ==================== GROSSING ====================

export function useGrossingQueue(
  filters?: CaseFilters,
  options?: Omit<UseQueryOptions<WorkflowQueueCase[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["grossing-queue", filters],
    queryFn: () => grossingService.getQueue(filters),
    refetchInterval: 30000,
    ...options,
  });
}

export function useCaseForGrossing(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-for-grossing", caseId],
    queryFn: () => grossingService.getCaseForGrossing(caseId!),
    enabled: !!caseId,
  });
}

export function useStartGrossing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => grossingService.startGrossing(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-grossing", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["grossing-queue"] });
    },
  });
}

export function useUpdateSpecimenGrossing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      specimenId,
      data,
    }: {
      caseId: string;
      specimenId: string;
      data: SpecimenGrossingUpdate;
    }) => grossingService.updateSpecimenGrossing(caseId, specimenId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distribution-case-slides"] });
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
    },
  });
}

// ==================== SCANNER ====================

export function useConfirmScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => scannerService.confirmScan(caseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-counts"] });
      broadcastWorkflowEvent("case-scanned");
    },
  });
}

export function useCompleteGrossing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => grossingService.completeGrossing(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-grossing", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["grossing-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

// ==================== GROSSING BLOCKS ====================

export function useSpecimenBlocks(
  specimenId: string | undefined,
  options?: Omit<UseQueryOptions<GrossingBlock[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["specimen-blocks", specimenId],
    queryFn: () => grossingService.getSpecimenBlocks(specimenId!),
    enabled: !!specimenId,
    ...options,
  });
}

export function useAddGrossingBlocks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      specimenId,
      count,
    }: {
      caseId: string;
      specimenId: string;
      count?: number;
    }) => grossingService.addBlocks(caseId, specimenId, count),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["specimen-blocks", variables.specimenId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-grossing", variables.caseId],
      });
    },
  });
}

export function useDeleteGrossingBlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      caseId: string;
      blockId: string;
      specimenId: string;
    }) => grossingService.deleteBlock(variables.caseId, variables.blockId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["specimen-blocks", variables.specimenId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-for-grossing", variables.caseId],
      });
    },
  });
}

export function usePrintBlockLabels() {
  return useMutation({
    mutationFn: ({
      caseId,
      blockIds,
    }: {
      caseId: string;
      blockIds: string[];
    }) => grossingService.printBlockLabels(caseId, blockIds),
  });
}

// ==================== EMBEDDING ====================

export function useEmbeddingQueue(
  filters?: CaseFilters,
  options?: Omit<UseQueryOptions<WorkflowQueueCase[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["embedding-queue", filters],
    queryFn: () => embeddingService.getQueue(filters),
    refetchInterval: 30000,
    ...options,
  });
}

export function useCaseForEmbedding(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-for-embedding", caseId],
    queryFn: () => embeddingService.getCaseForEmbedding(caseId!),
    enabled: !!caseId,
  });
}

export function useStartEmbedding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => embeddingService.startEmbedding(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-embedding", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["embedding-queue"] });
    },
  });
}

export function useCompleteEmbedding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => embeddingService.completeEmbedding(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-embedding", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["embedding-queue"] });
    },
  });
}

export function useMarkBlockAsEmbedded() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      specimenNumber,
      blockNumber,
    }: {
      caseId: string;
      specimenNumber: string;
      blockNumber: string;
    }) =>
      embeddingService.markBlockAsEmbedded(caseId, specimenNumber, blockNumber),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-embedding", caseId],
      });
    },
  });
}

// ==================== CUTTING ====================

export function useCuttingQueue(
  filters?: CaseFilters,
  options?: Omit<UseQueryOptions<WorkflowQueueCase[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["cutting-queue", filters],
    queryFn: () => cuttingService.getQueue(filters),
    refetchInterval: 30000,
    ...options,
  });
}

export function useCuttingQueuePage(
  request: Parameters<typeof cuttingService.getQueuePage>[0]
) {
  return useQuery({
    queryKey: ["cutting-queue-page", request],
    queryFn: () => cuttingService.getQueuePage(request),
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });
}

export function useCuttingQueueContext(caseId: string | undefined) {
  return useQuery({
    queryKey: ["cutting-queue-context", caseId],
    queryFn: () => cuttingService.getQueueContext(caseId!),
    enabled: !!caseId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useCaseForCutting(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-for-cutting", caseId],
    queryFn: () => cuttingService.getCaseForCutting(caseId!),
    enabled: !!caseId,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  });
}

export function useStartCutting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => cuttingService.startCutting(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-cutting", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-page"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-context"] });
    },
  });
}

export function useProcessCassetteScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ caseId, blockId }: { caseId: string; blockId: string }) =>
      cuttingService.processCassetteScan(caseId, blockId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-cutting", variables.caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-page"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-context"] });
    },
  });
}

export function useReprintSlides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slideIds: string[]) => cuttingService.reprintSlides(slideIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["case-for-cutting"] });
    },
  });
}

export function useCompleteCutting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => cuttingService.completeCutting(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-cutting", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-page"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-context"] });
    },
  });
}

export function useCancelCutting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => cuttingService.cancelCutting(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-cutting", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-page"] });
      queryClient.invalidateQueries({ queryKey: ["cutting-queue-context"] });
    },
  });
}

// ==================== STAINING ====================

export function useStainingQueue(
  includeCompleted = false,
  filters?: CaseFilters,
  options?: Omit<UseQueryOptions<WorkflowQueueCase[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["staining-queue", includeCompleted, filters],
    queryFn: () => stainingService.getQueue(includeCompleted, filters),
    refetchInterval: 30000,
    ...options,
  });
}

export function useStainingSlides(
  params: StainingSlidesParams,
  options?: Omit<
    UseQueryOptions<StainingSlidesResponse>,
    "queryKey" | "queryFn"
  >
) {
  const { category, view, page, limit, filters } = params;
  return useQuery({
    queryKey: ["staining-slides", category, view, page, limit, filters],
    queryFn: () => stainingService.getStainingSlides(params),
    refetchInterval: 30000,
    placeholderData: keepPreviousData,
    ...options,
  });
}

export function useDistributeIHCSlides() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (filters?: CaseFilters) =>
      stainingService.distributeIHCSlides(filters),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
      queryClient.invalidateQueries({ queryKey: ["staining-stats"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-progress"] });
    },
  });
}

export function useStainingStats() {
  return useQuery({
    queryKey: ["staining-stats"],
    queryFn: stainingService.getDistributionStats,
    refetchInterval: 30000,
  });
}

export function useVendorProgress() {
  return useQuery({
    queryKey: ["vendor-progress"],
    queryFn: stainingService.getVendorProgress,
    refetchInterval: 30000,
  });
}

export function useProcessNow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => stainingService.processNow(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
      queryClient.invalidateQueries({ queryKey: ["staining-stats"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-progress"] });
    },
  });
}

export function useRetryFailed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => stainingService.retryFailed(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
      queryClient.invalidateQueries({ queryKey: ["staining-stats"] });
    },
  });
}

export function useCompleteNonIHCStainingScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (barcode: string) =>
      stainingService.completeNonIHCStainingScan(barcode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
    },
  });
}

export function useBulkCompleteNonIHCStaining() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slideIds: string[]) =>
      stainingService.bulkCompleteNonIHCStaining(slideIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
    },
  });
}

export function useManualCompleteIHCStaining() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      slideId,
      notes,
    }: {
      slideId: string;
      caseId: string;
      notes?: string;
    }) => stainingService.manualCompleteIHCStaining(slideId, notes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
      queryClient.invalidateQueries({ queryKey: ["staining-stats"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-progress"] });
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-counts"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-case-slides"] });
      queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.invalidateQueries({ queryKey: ["case-stats"] });
      queryClient.invalidateQueries({ queryKey: ["case", variables.caseId] });
      queryClient.invalidateQueries({
        queryKey: ["case-status-history", variables.caseId],
      });
      queryClient.invalidateQueries({
        queryKey: ["case-slides", variables.caseId],
      });
    },
  });
}

export function useReassignSlideMachine() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      slideId,
      machineId,
    }: {
      slideId: string;
      machineId: string | null;
    }) => stainingService.reassignSlideMachine(slideId, machineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-progress"] });
    },
  });
}

export function useSetSlideStainerType() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      slideId,
      stainerType,
    }: {
      slideId: string;
      stainerType: "biocare" | "leica" | null;
    }) => stainingService.setSlideStainerType(slideId, stainerType),
    // Optimistically reflect the new stainer in the UI immediately, so the
    // Select does not wait 1-2s for the backend round-trip before updating.
    // The staining page caches a paginated { data, counts } envelope under
    // the "staining-slides" key, so patch the rows inside `data`.
    onMutate: async ({
      slideId,
      stainerType,
    }: {
      slideId: string;
      stainerType: "biocare" | "leica" | null;
    }) => {
      const queryKey = ["staining-slides"];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueriesData({ queryKey });
      const applyStainer = (item: any) =>
        item && item.id === slideId ? { ...item, stainerType } : item;
      queryClient.setQueriesData({ queryKey }, (old: unknown) => {
        const response = old as StainingSlidesResponse | undefined;
        if (!response || !Array.isArray(response.data)) return old;
        return { ...response, data: response.data.map(applyStainer) };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      // Roll back to the snapshot captured before the optimistic update.
      context?.previous.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-progress"] });
    },
  });
}

export function useResendIhcSlide() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slideId: string) => stainingService.resendIhcSlide(slideId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["staining-slides"] });
      queryClient.invalidateQueries({ queryKey: ["staining-queue"] });
      queryClient.invalidateQueries({ queryKey: ["staining-stats"] });
      queryClient.invalidateQueries({ queryKey: ["vendor-progress"] });
    },
  });
}

// ==================== DISTRIBUTION ====================

export function useCasesPendingDistribution() {
  return useQuery({
    queryKey: ["pending-distribution"],
    queryFn: distributionService.getPendingCases,
    refetchInterval: 30000,
  });
}

export function useDistributionQueue(
  request: Parameters<typeof distributionService.getQueue>[0]
) {
  return useQuery({
    queryKey: ["distribution-queue", request],
    queryFn: () => distributionService.getQueue(request),
    placeholderData: keepPreviousData,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });
}

export function useDistributionQueueCounts(
  options?: Omit<
    UseQueryOptions<DistributionQueueCounts>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["distribution-counts"],
    queryFn: distributionService.getQueueCounts,
    refetchInterval: 30000,
    ...options,
  });
}

export function useProcessDistributionScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      barcode,
      expectedCaseId,
    }: {
      barcode: string;
      expectedCaseId?: string;
    }) => distributionService.processScan(barcode, expectedCaseId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
    },
  });
}

export function useDistributionPathologists() {
  return useQuery({
    queryKey: ["distribution-pathologists"],
    queryFn: distributionService.getPathologists,
  });
}

export function useAssignPathologistAtDistribution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      pathologistId,
    }: {
      caseId: string;
      pathologistId: string | null;
    }) => distributionService.assignPathologist(caseId, pathologistId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
      queryClient.invalidateQueries({
        queryKey: ["distribution-case-slides"],
      });
    },
  });
}

export function useConfirmDistribution() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseIds: string[]) =>
      distributionService.confirmDistribution(caseIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-counts"] });
    },
  });
}

export function useDistributionCaseSlides(caseId: string | null) {
  return useQuery({
    queryKey: ["distribution-case-slides", caseId],
    queryFn: () => distributionService.getCaseSlides(caseId!),
    enabled: !!caseId,
  });
}

export function useMarkSlideReadyForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slideId: string) =>
      distributionService.markSlideReady(slideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distribution-case-slides"] });
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
    },
  });
}

export function useMarkSlideNotReadyForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (slideId: string) =>
      distributionService.markSlideNotReady(slideId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["distribution-case-slides"] });
      queryClient.invalidateQueries({ queryKey: ["pending-distribution"] });
      queryClient.invalidateQueries({ queryKey: ["distribution-queue"] });
    },
  });
}

// ==================== PATHOLOGIST REVIEW ====================

export function usePathologistReviewPrefixOptions(
  userId: string | undefined,
  enabled: boolean,
  options: Pick<PathologistReviewQueueFilters, "queueScope" | "partition"> = {}
) {
  const { queueScope, partition } = options;
  return useQuery({
    queryKey: [
      "pathologist-review-prefix-options",
      userId,
      queueScope,
      partition,
    ],
    queryFn: () =>
      pathologistReviewService.getPrefixOptions({ queueScope, partition }),
    enabled: enabled && !!userId,
    refetchInterval: 30000,
  });
}

export function usePathologistReviewQueue(
  filters?: PathologistReviewQueueFilters,
  options?: Omit<
    UseQueryOptions<PathologistReviewQueueCase[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["pathologist-review-queue", filters],
    queryFn: () => pathologistReviewService.getQueue(filters),
    refetchInterval: 30000,
    ...options,
  });
}

export function useInfinitePathologistReviewQueue(
  filters: PathologistReviewQueueFilters = {},
  options: { enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    queryKey: ["pathologist-review-queue", "paged", filters],
    queryFn: ({ pageParam }) =>
      pathologistReviewService.getPagedQueue(filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled: options.enabled,
    staleTime: 30000,
  });
}

export function usePathologistReviewStats(
  pathologistId?: string,
  options?: Omit<
    UseQueryOptions<PathologistReviewStats>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["pathologist-review-stats", pathologistId],
    queryFn: () => pathologistReviewService.getStats(pathologistId),
    refetchInterval: 30000,
    ...options,
  });
}

export function usePathologistCompletedCases(
  options?: Omit<
    UseQueryOptions<PathologistReviewQueueCase[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["pathologist-completed-cases"],
    queryFn: () => pathologistReviewService.getCompletedCases(),
    refetchInterval: 30000,
    ...options,
  });
}

export function useInfinitePathologistCompletedCases(
  filters: PathologistCompletedQueueFilters = {},
  options: { enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    queryKey: ["pathologist-completed-cases", "paged", filters],
    queryFn: ({ pageParam }) =>
      pathologistReviewService.getPagedCompletedCases(filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled: options.enabled,
    staleTime: 30000,
  });
}

export function usePathologistIncomingQueue(
  pathologistId?: string,
  options?: Omit<
    UseQueryOptions<IncomingLabQueueResponse>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["pathologist-incoming-queue", pathologistId],
    queryFn: () => pathologistReviewService.getIncomingQueue(pathologistId),
    refetchInterval: 30000,
    ...options,
  });
}

export function useInfinitePathologistIncomingQueue(
  filters: PathologistIncomingQueueFilters = {},
  options: { enabled?: boolean } = {}
) {
  return useInfiniteQuery({
    queryKey: ["pathologist-incoming-queue", "paged", filters],
    queryFn: ({ pageParam }) =>
      pathologistReviewService.getPagedIncomingQueue(filters, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    enabled: options.enabled,
    staleTime: 30000,
  });
}

export function useCaseForPathologistReview(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-for-pathologist-review", caseId],
    queryFn: () =>
      pathologistReviewService.getCaseForPathologistReview(caseId!),
    enabled: !!caseId,
  });
}

// ==================== PATHOLOGIST COMMENTS ====================

export function usePathologistComments(caseId: string | undefined) {
  return useQuery<PathologistComment[]>({
    queryKey: ["pathologist-comments", caseId],
    queryFn: () => pathologistReviewService.getPathologistComments(caseId!),
    enabled: !!caseId,
  });
}

export function useAddPathologistComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      commentText,
    }: {
      caseId: string;
      commentText: string;
    }) => pathologistReviewService.addPathologistComment(caseId, commentText),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["pathologist-comments", variables.caseId],
      });
    },
  });
}

export function useDeletePathologistComment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      commentId,
    }: {
      caseId: string;
      commentId: string;
    }) => pathologistReviewService.deletePathologistComment(caseId, commentId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["pathologist-comments", variables.caseId],
      });
    },
  });
}

export function useUpdateCaseRequisition() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      data,
    }: {
      caseId: string;
      data: { patient?: Record<string, any>; case?: Record<string, any> };
    }) => pathologistReviewService.updateCaseRequisition(caseId, data),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
    },
  });
}

export function useStartPathologistReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) =>
      pathologistReviewService.startPathologistReview(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
    },
  });
}

export function useAcknowledgeAdditionalStainNoReportChange() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      eventId,
      expectedAssignmentRevision,
      requestId,
    }: {
      caseId: string;
      eventId: string;
      expectedAssignmentRevision: number;
      requestId: string;
    }) =>
      pathologistReviewService.acknowledgeAdditionalStainNoReportChange(
        caseId,
        { eventId, expectedAssignmentRevision, requestId }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", variables.caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-stats"] });
    },
  });
}

export function useCompleteTcPathologistReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      expectedAssignmentRevision,
    }: {
      caseId: string;
      expectedAssignmentRevision: number;
    }) =>
      pathologistReviewService.completeTcReview(
        caseId,
        expectedAssignmentRevision
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-stats"] });
    },
  });
}

export function useCompleteTcExternalAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      expectedAssignmentRevision,
    }: {
      caseId: string;
      expectedAssignmentRevision: number;
    }) =>
      pathologistReviewService.completeTcExternalAction(
        caseId,
        expectedAssignmentRevision
      ),
    onSuccess: (_result, { caseId }) => {
      queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      queryClient.invalidateQueries({
        queryKey: ["case-status-history", caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["pathologist-review-stats"] });
    },
  });
}

export function useDiagnosisMacros() {
  return useQuery({
    queryKey: ["diagnosis-macros"],
    queryFn: () => pathologistReviewService.getDiagnosisMacros(),
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export function useDiagnosisCategories(enabled = true) {
  return useQuery({
    queryKey: ["diagnosis-categories"],
    queryFn: () => pathologistReviewService.getDiagnosisCategories(),
    enabled,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

export async function invalidateDiagnosisDraftQueries(
  queryClient: QueryClient,
  caseId: string
): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: ["case-for-pathologist-review", caseId],
    }),
    queryClient.invalidateQueries({
      queryKey: ["stain-requests", "pending", caseId],
    }),
    queryClient.invalidateQueries({ queryKey: ["report-preview", caseId] }),
    queryClient.invalidateQueries({ queryKey: ["document-view", caseId] }),
    queryClient.invalidateQueries({
      queryKey: ["report-review-status", caseId],
    }),
  ]);
}

export function useSaveDiagnosisDraft() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      diagnoses,
      grossing,
      caseLevelData,
    }: {
      caseId: string;
      diagnoses: Array<{
        specimenId: string;
        macroCode?: string | null;
        diagnosisText?: string;
        microscopicDescription?: string;
        location?: string;
        category?: string;
        cptCode?: string;
        icdCode?: string | null;
        frozenInterpretation?: string;
        color?: string;
        sizeCm?: string;
        sizeMm?: string;
        consistency?: string;
      }>;
      grossing?: Array<{
        specimenId: string;
        grossCodeId?: string;
        grossDescription?: string;
        sourceId?: string | null;
        subsiteId?: string | null;
        collectionMethod?: string | null;
        findings?: string | null;
        material?: string | null;
        fragmentCount?: number | null;
        fragmentCountQualifier?: FragmentCountQualifier | null;
      }>;
      caseLevelData?: { frozenInterpretation?: string };
    }) =>
      pathologistReviewService.saveDiagnosisDraft(
        caseId,
        diagnoses,
        grossing,
        caseLevelData
      ),
    onSuccess: (_, { caseId }) =>
      invalidateDiagnosisDraftQueries(queryClient, caseId),
  });
}

export function useAddSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      data,
    }: {
      caseId: string;
      data: {
        sourceId?: string | null;
        subsiteId?: string | null;
        material?: string | null;
        collectionMethod?: string | null;
        gross?: string | null;
        comments?: string | null;
      };
    }) => pathologistReviewService.addSpecimen(caseId, data),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
    },
  });
}

export function useArchiveSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      specimenId,
    }: {
      caseId: string;
      specimenId: string;
    }) => pathologistReviewService.archiveSpecimen(caseId, specimenId),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
    },
  });
}

export function useRecoverSpecimen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      specimenId,
    }: {
      caseId: string;
      specimenId: string;
    }) => pathologistReviewService.recoverSpecimen(caseId, specimenId),
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: ["case-for-pathologist-review", caseId],
      });
    },
  });
}

// ==================== QA REVIEW QUEUE ====================

export function useQaReviewQueue(
  filters?: QaReviewQueueFilters,
  options?: Omit<UseQueryOptions<QaReviewQueueItem[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["qa-review-queue", filters],
    queryFn: () => qaReviewService.getQueue(filters),
    refetchInterval: 30000,
    ...options,
  });
}

export function useQaReviewQueueStats(
  reviewerId?: string,
  options?: Omit<UseQueryOptions<QaReviewQueueStats>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["qa-review-stats", reviewerId],
    queryFn: () => qaReviewService.getStats(reviewerId),
    refetchInterval: 30000,
    ...options,
  });
}

// ==================== QA REVIEW DETAIL (for QA Comparison Form) ====================

/**
 * Hook to fetch QA review details for the QA Comparison Form
 */
export function useQaReviewDetail(
  qaReviewId: string | undefined,
  options?: Omit<UseQueryOptions<QaReviewDetail>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["qa-review-detail", qaReviewId],
    queryFn: () => qaReviewDetailService.getById(qaReviewId!),
    enabled: !!qaReviewId,
    ...options,
  });
}

/**
 * Hook to update QA review (save draft)
 */
export function useUpdateQaReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      qaReviewId,
      data,
    }: {
      qaReviewId: string;
      data: QaReviewUpdateData;
    }) => qaReviewDetailService.update(qaReviewId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["qa-review-detail", variables.qaReviewId],
      });
    },
  });
}

/**
 * Hook to complete QA review
 */
export function useCompleteQaReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      qaReviewId,
      data,
    }: {
      qaReviewId: string;
      data: QaReviewCompleteData;
    }) => qaReviewDetailService.complete(qaReviewId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["qa-review-detail", variables.qaReviewId],
      });
      // Also invalidate the queue since this review is now completed
      queryClient.invalidateQueries({ queryKey: ["qa-review-queue"] });
      queryClient.invalidateQueries({ queryKey: ["qa-review-stats"] });
    },
  });
}

// ==================== SHIPPING ====================

export function useCasesPendingShipping() {
  return useQuery<PendingShippingCase[]>({
    queryKey: ["pending-shipping"],
    queryFn: shippingService.getPendingCases,
    refetchInterval: 30000,
  });
}

export function useConfirmShipping() {
  const queryClient = useQueryClient();
  return useMutation<
    ConfirmShippingResult,
    Error,
    string[] | { caseIds: string[]; trackingNumber: string }
  >({
    mutationFn: (request) =>
      Array.isArray(request)
        ? shippingService.confirmShipping(request)
        : shippingService.confirmShipping(
            request.caseIds,
            request.trackingNumber
          ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-shipping"] });
      queryClient.invalidateQueries({ queryKey: ["shipped-cases"] });
    },
  });
}

export function useShippedCases(filters: ShippedCasesFilters) {
  return useQuery<ShippedCasesResponse>({
    queryKey: ["shipped-cases", filters],
    queryFn: () => shippingService.getShippedCases(filters),
    placeholderData: keepPreviousData,
  });
}

export function useUpdateShippedCaseTracking() {
  const queryClient = useQueryClient();
  return useMutation<
    ShippingTrackingUpdate,
    Error,
    { caseId: string; trackingNumber: string | null }
  >({
    mutationFn: ({ caseId, trackingNumber }) =>
      shippingService.updateShippedCaseTracking(caseId, trackingNumber),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shipped-cases"] });
    },
  });
}

// ==================== ERROR REPORTS ====================

export function useErrorReports(
  filters?: ErrorReportFilters,
  options?: Omit<UseQueryOptions<ErrorReport[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["error-reports", filters],
    queryFn: () => errorReportsService.getReports(filters),
    refetchInterval: 30000,
    ...options,
  });
}

// Keep for backwards compatibility
export function usePendingErrorReports(
  options?: Omit<UseQueryOptions<ErrorReport[]>, "queryKey" | "queryFn">
) {
  return useErrorReports({ status: "pending" }, options);
}

export function useCreateErrorReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      caseId: string;
      errorId: string;
      description: string;
      blockCase?: boolean;
    }) => errorReportsService.createReport(data),
    onSuccess: () => {
      // Invalidate all error report queries
      queryClient.invalidateQueries({ queryKey: ["error-reports"] });
      queryClient.invalidateQueries({ queryKey: ["pending-errors"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
    },
  });
}

export function useResolveErrorReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      errorId,
      data,
    }: {
      errorId: string;
      data: { resolutionNotes: string; correctionRequestId?: string | null };
    }) => errorReportsService.resolveReport(errorId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["error-reports"] });
      queryClient.invalidateQueries({ queryKey: ["pending-errors"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] });
      queryClient.invalidateQueries({ queryKey: ["all-correction-requests"] });
    },
  });
}

// ==================== CASE LOCK ====================

export function useCaseLock(caseId: string | undefined) {
  return useQuery({
    queryKey: ["case-lock", caseId],
    queryFn: () => caseLockService.getLockInfo(caseId!),
    enabled: !!caseId,
    refetchInterval: 60000,
  });
}

export function useAcquireLock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      workflowStep,
      reason,
    }: {
      caseId: string;
      workflowStep: string;
      reason?: string;
    }) => caseLockService.acquireLock(caseId, workflowStep, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["case-lock", variables.caseId],
      });
    },
  });
}

export function useReleaseLock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => caseLockService.releaseLock(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({ queryKey: ["case-lock", caseId] });
    },
  });
}

export function useExtendLock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => caseLockService.extendLock(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({ queryKey: ["case-lock", caseId] });
    },
  });
}

export function useAllLocks() {
  return useQuery({
    queryKey: ["all-locks"],
    queryFn: () => caseLockService.getAllLocks(),
    refetchInterval: 30000,
  });
}

export function useForceReleaseLock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (caseId: string) => caseLockService.forceReleaseLock(caseId),
    onSuccess: (_, caseId) => {
      queryClient.invalidateQueries({ queryKey: ["case-lock", caseId] });
      queryClient.invalidateQueries({ queryKey: ["all-locks"] });
    },
  });
}

// ==================== BARCODE ====================

export function useOpenCaseByBarcode() {
  return useMutation({
    mutationFn: ({ barcode, intent }: { barcode: string; intent?: string }) =>
      barcodeService.openCaseByBarcode(barcode, intent),
  });
}

// ==================== CONTROL CONFIRMATION ====================

export function useControlConfirmationStatus(
  options?: Omit<
    UseQueryOptions<ControlConfirmationStatus>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["control-confirmation-status"],
    queryFn: controlConfirmationService.getStatus,
    refetchInterval: 60000, // Check every minute
    ...options,
  });
}

export function useHeConfirmationDetail(
  date?: string,
  options?: Omit<UseQueryOptions<HeConfirmationDetail>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["he-confirmation-detail", date],
    queryFn: () => controlConfirmationService.getDetailedStatus(date),
    ...options,
  });
}

export function usePracticesForConfirmation(
  options?: Omit<
    UseQueryOptions<PracticeForConfirmation[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["practices-for-confirmation"],
    queryFn: controlConfirmationService.getPractices,
    ...options,
  });
}

export function useCreateControlConfirmation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (practiceIds: string[]) =>
      controlConfirmationService.confirm(practiceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["control-confirmation-status"],
      });
      queryClient.invalidateQueries({
        queryKey: ["he-confirmation-detail"],
      });
    },
  });
}

// ==================== CONTROL SLIDES LIST ====================

/** Legacy slide-level control slides (still used by distribution for individual slide display) */
export function useControlSlides(
  filters: { date?: string; stainId?: string; practiceId?: string },
  options?: Omit<UseQueryOptions<any[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["control-slides", filters],
    queryFn: () => controlSlidesService.getControlSlides(filters),
    ...options,
  });
}

// ==================== STANDALONE STAIN CONTROLS (new model) ====================

export function useStainsOfDay(
  date?: string,
  options?: Omit<UseQueryOptions<StainOfDayGroup[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["control-slides-stains-of-day", date],
    queryFn: () => controlSlidesService.getStainsOfDay(date),
    ...options,
  });
}

export function useControlSummary(date?: string) {
  return useQuery({
    queryKey: ["control-slides-summary", date],
    queryFn: () => controlSlidesService.getControlSummary(date),
  });
}

export function useConfirmPresence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      stainId,
      poolId,
      scannedId,
    }: {
      stainId: string;
      poolId: string | null;
      scannedId?: string;
    }) => controlSlidesService.confirmPresence(stainId, poolId, scannedId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["control-slides-stains-of-day"],
      });
      queryClient.invalidateQueries({ queryKey: ["control-slides-summary"] });
    },
  });
}

export function useUndoPresence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      stainId,
      poolId,
    }: {
      stainId: string;
      poolId: string | null;
    }) => controlSlidesService.undoPresence(stainId, poolId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["control-slides-stains-of-day"],
      });
      queryClient.invalidateQueries({ queryKey: ["control-slides-summary"] });
    },
  });
}

export function useSetControlResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      stainId,
      poolId,
      result,
    }: {
      stainId: string;
      poolId: string | null;
      result: "pass" | "fail";
    }) => controlSlidesService.setResult(stainId, poolId, result),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["control-slides-stains-of-day"],
      });
      queryClient.invalidateQueries({ queryKey: ["control-slides-summary"] });
    },
  });
}

export function useUndoControlResult() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      stainId,
      poolId,
    }: {
      stainId: string;
      poolId: string | null;
    }) => controlSlidesService.undoResult(stainId, poolId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["control-slides-stains-of-day"],
      });
      queryClient.invalidateQueries({ queryKey: ["control-slides-summary"] });
    },
  });
}

// ==================== QA METRICS REPORTING ====================

export function useQaMetricsSummary(
  filters?: QaMetricsFilters,
  options?: Omit<UseQueryOptions<QaMetricsSummary>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["qa-metrics-summary", filters],
    queryFn: () => qaMetricsService.getSummary(filters),
    ...options,
  });
}

export function useQaMetricsTrends(
  filters?: QaMetricsFilters,
  options?: Omit<UseQueryOptions<QaMetricsTrendPoint[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["qa-metrics-trends", filters],
    queryFn: () => qaMetricsService.getTrends(filters),
    ...options,
  });
}

export function useQaMetricsByPathologist(
  filters?: QaMetricsFilters,
  options?: Omit<
    UseQueryOptions<QaMetricsByPathologist[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["qa-metrics-by-pathologist", filters],
    queryFn: () => qaMetricsService.getByPathologist(filters),
    ...options,
  });
}

export function useQaMetricsByInstitution(
  filters?: QaMetricsFilters,
  options?: Omit<
    UseQueryOptions<QaMetricsByInstitution[]>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["qa-metrics-by-institution", filters],
    queryFn: () => qaMetricsService.getByInstitution(filters),
    ...options,
  });
}

// ==================== REPORT DELIVERY QUEUE ====================

export function useDeliveryQueue(
  request: DeliveryQueuePageRequest,
  options?: Omit<UseQueryOptions<DeliveryQueuePage>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["delivery-queue", "v2", request],
    queryFn: () => reportDeliveryQueueService.getQueue(request),
    placeholderData: keepPreviousData,
    refetchInterval: 15000, // Refresh every 15 seconds
    ...options,
  });
}

export function useDeliveryQueueStats(
  options?: Omit<UseQueryOptions<DeliveryQueueStats>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["delivery-queue-stats"],
    queryFn: () => reportDeliveryQueueService.getStats(),
    refetchInterval: 15000, // Refresh every 15 seconds
    ...options,
  });
}

export function useSuppressedDeliveryMethods(
  options?: Omit<
    UseQueryOptions<DeliverySuppressionSettings>,
    "queryKey" | "queryFn"
  >
) {
  return useQuery({
    queryKey: ["delivery-queue-suppressed-methods"],
    queryFn: () => reportDeliveryQueueService.getSuppressedMethods(),
    refetchInterval: 15000, // Refresh every 15 seconds
    ...options,
  });
}

export function useUpdateSuppressedDeliveryMethods() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (methodKeys: string[]) =>
      reportDeliveryQueueService.updateSuppressedMethods(methodKeys),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["delivery-queue-suppressed-methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
    },
  });
}

export function useBulkCancelDeliveries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryIds: string[]) =>
      reportDeliveryQueueService.bulkCancel(deliveryIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
    },
  });
}

export function useBulkRetryDeliveries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryIds: string[]) =>
      reportDeliveryQueueService.bulkRetry(deliveryIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
    },
  });
}

export function useBulkResendDeliveries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryIds: string[]) =>
      reportDeliveryQueueService.bulkResend(deliveryIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
    },
  });
}

export function useProcessDeliveries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => reportDeliveryQueueService.processNow(),
    onSuccess: () => {
      // Invalidate immediately
      queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });

      // Also refetch after delays to catch background processing updates
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
        queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
      }, 2000);

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
        queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
      }, 5000);
    },
  });
}

export function useProcessSelectedDeliveries() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (deliveryIds: string[]) =>
      reportDeliveryQueueService.processSelected(deliveryIds),
    onSuccess: () => {
      // Invalidate immediately
      queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });

      // Also refetch after delays to catch background processing updates
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
        queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
      }, 2000);

      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
        queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
      }, 5000);
    },
  });
}

// ==================== SEND REPORT (MANUAL DELIVERY) ====================

export function useActiveDeliveryMethodsForCase(caseId?: string) {
  return useQuery<CaseDeliveryMethodConfig[]>({
    queryKey: ["case-delivery-methods", caseId],
    queryFn: () => getActiveDeliveryMethodsForCase(caseId!),
    enabled: !!caseId,
    staleTime: 30_000,
  });
}

export function useQueueManualDelivery() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      caseId,
      deliveryConfigIds,
    }: {
      caseId: string;
      deliveryConfigIds: string[];
    }) => queueManualDelivery(caseId, deliveryConfigIds),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["case-delivery-methods", variables.caseId],
      });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue"] });
      queryClient.invalidateQueries({ queryKey: ["delivery-queue-stats"] });
      queryClient.invalidateQueries({
        queryKey: reportDeliveryRecallQueryKey(variables.caseId),
      });
    },
  });
}

export function useDeliveryAdmins() {
  return useQuery<DeliveryAdmin[]>({
    queryKey: ["delivery-admins"],
    queryFn: getDeliveryAdmins,
    staleTime: 5 * 60 * 1000,
  });
}

// ==================== STAIN METRICS REPORTING ====================

export function useStainMetricsSummary(
  filters?: StainMetricsFilters,
  options?: Omit<UseQueryOptions<StainMetricsSummary>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["stain-metrics-summary", filters],
    queryFn: () => stainMetricsService.getSummary(filters),
    ...options,
  });
}

export function useStainMetricsTrends(
  filters?: StainMetricsFilters,
  options?: Omit<UseQueryOptions<StainMetricsTrends>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: ["stain-metrics-trends", filters],
    queryFn: () => stainMetricsService.getTrends(filters),
    ...options,
  });
}

export function useUserPreference(key: string) {
  return useQuery({
    queryKey: ["user-preference", key],
    queryFn: () => userPreferencesService.get(key),
    staleTime: Infinity,
  });
}

export function useSaveUserPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: Record<string, any> }) =>
      userPreferencesService.save(key, value),
    onSuccess: (_, { key }) => {
      queryClient.invalidateQueries({ queryKey: ["user-preference", key] });
    },
  });
}
